// The creator's "Change owner" control on a Draft (owner finding 2026-09-02: a Task could be owned only by the
// person who asked for it). Rendered ONLY from the server-derived `canChangeOwner` affordance; it offers the
// active Team directory by display NAME and posts that name — the server resolves who it is and Tasks
// re-checks the exact creator, the Draft state and the revision. The client never chooses or sends a User id.

import { useEffect, useState } from 'react';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import type { TaskDetailProjection } from '../surfaces/know-projections.ts';
import { notifyTaskProjectionsChanged } from '../surfaces/task-projection-events.ts';
import { Btn } from '../surfaces/primitives.tsx';

interface TaskOwnerControlProps {
  readonly detail: TaskDetailProjection;
  readonly reload: () => void;
}

/** The active Team directory's other names (null while loading); the current owner is not offered back. */
function useOtherActiveNames(enabled: boolean, currentOwner: string): readonly string[] | null {
  const api = useSurfaceApi();
  const [names, setNames] = useState<readonly string[] | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    api.getDirectory().then(
      (directory) => {
        if (cancelled) return;
        setNames(
          directory.users
            .filter((user) => user.status === 'active' && user.displayName !== currentOwner)
            .map((user) => user.displayName),
        );
      },
      () => {
        if (!cancelled) setNames([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, enabled, currentOwner]);
  return names;
}

/** Route the owner change and reconcile to the server-owned snapshot only after the server accepts it. */
function useOwnerChange(
  detail: TaskDetailProjection,
  reload: () => void,
): {
  readonly busy: boolean;
  readonly note: string | null;
  readonly submit: (name: string) => void;
} {
  const api = useSurfaceApi();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const submit = async (name: string): Promise<void> => {
    if (busy || name === '') return;
    setBusy(true);
    setNote(null);
    try {
      const result = await api.reassignTaskOwner(detail.id, detail.definitionRevision, name);
      if (result.ok) {
        notifyTaskProjectionsChanged();
        reload();
      } else setNote(result.note ?? 'That owner change was not permitted.');
    } catch {
      setNote('That owner change could not reach the task. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, submit: (name) => void submit(name) };
}

export function TaskOwnerControl({
  detail,
  reload,
}: TaskOwnerControlProps): React.JSX.Element | null {
  const [chosen, setChosen] = useState('');
  const names = useOtherActiveNames(detail.canChangeOwner, detail.ownerDisplayName);
  const change = useOwnerChange(detail, reload);
  if (!detail.canChangeOwner) return null;
  return (
    <form
      className="task-review-form"
      aria-label="Change task owner"
      onSubmit={(event) => {
        event.preventDefault();
        change.submit(chosen);
      }}
    >
      <label>
        Owner
        <select
          value={chosen}
          required
          disabled={change.busy || names === null}
          onChange={(event) => setChosen(event.target.value)}
        >
          <option value="">
            {names === null
              ? 'Loading the Team directory…'
              : `Currently ${detail.ownerDisplayName}`}
          </option>
          {(names ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <div className="task-review-form-actions">
        <Btn type="submit" sm disabled={change.busy || chosen === ''}>
          Change owner
        </Btn>
      </div>
      {change.note !== null ? (
        <p role="status" className="gate-note task-review-note">
          {change.note}
        </p>
      ) : null}
    </form>
  );
}
